<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const tiers = [
    {
      title: 'Design Partner Program',
      description: 'Working closely with early adopters to shape the platform. Priority support, direct access to the founding team, and influence over the roadmap.',
      cta: 'Apply',
      href: '/partners',
      highlight: false
    },
    {
      title: 'Azure Marketplace',
      description: 'Deploy Corral directly from the Azure Marketplace into your tenant. Platform subscription plus your Azure infrastructure costs — both count against your existing committed spend (MACC).',
      cta: 'Deploy on Azure',
      href: 'https://azuremarketplace.microsoft.com',
      highlight: true
    },
    {
      title: 'Enterprise & Embedded',
      description: 'Custom deployment, SLA, dedicated support, and embedded licensing for product companies building on Corral.',
      cta: 'Talk to us',
      href: '/company/contact',
      highlight: false
    }
  ];
</script>

<PageMeta
  title="Pricing — Start deploying. Scale when you're ready."
  description="Deploy Corral from Azure Marketplace into your tenant. No long-term contracts. No procurement cycles. Design Partner Program available for early adopters."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto text-center">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Start deploying. Scale when you're ready.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Available through Azure Marketplace. No long-term contracts. No procurement cycles. No seven-figure commitments.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- Tier cards -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-3 gap-0">
          {#each tiers as tier, i}
            <div class="bg-[#171719] border border-[#333335] p-8 flex flex-col {i > 0 ? 'lg:-ml-px' : ''} {i >= 1 ? '-mt-px lg:mt-0' : ''} {tier.highlight ? 'ring-1 ring-[#ee6018] relative z-10' : ''}">
              <h3 class="font-sans text-[20px] text-white mb-4">{tier.title}</h3>
              <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed mb-8 flex-1">
                {tier.description}
              </p>
              <a
                href={tier.href}
                class="{tier.highlight
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-transparent text-white border border-[#333335] hover:border-[#ee6018]/40'
                } font-mono text-[14px] px-5 py-3 text-center transition-colors"
              >
                {tier.cta}
              </a>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>
